import React, { memo, useCallback } from 'react';
import * as Clipboard from 'expo-clipboard';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';

import { LI, UL } from '@expo/html-elements';
import { theme } from '@garnish/constants';
import { getLocationAddressGeolocationLink } from '@garnish/location';
import { useNoticeBannersStackActions } from '@garnish/overlays';

import { LocationResultsCardCTAButton, LocationResultsCardCTALink } from './subcomponents';

import type { Location } from '../../LocationResultCard.types';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultCardCTAs = memo((props: LocationResultCardCTAsProps) => {
  const { phone, address, lng, lat } = props.location;

  const shouldRenderCallCTA = phone !== undefined;
  const shouldRenderUberCTA = address !== undefined && lat !== undefined && lng !== undefined;

  return (
    <UL style={styles.infoCTAList}>
      {shouldRenderCallCTA ? (
        <LI style={styles.infoCTAListItem}>
          <LocationResultsCardPhoneCTA phone={phone} />
        </LI>
      ) : null}

      <LI style={styles.infoCTAListItem}>
        <LocationResultsCardDirectionsCTA address={address} />
      </LI>

      {shouldRenderUberCTA ? (
        <LI style={styles.infoCTAListItem}>
          <LocationResultsCardUberCTA address={address} lng={lng} lat={lat} />
        </LI>
      ) : null}

      <LI style={styles.infoCTAListItem}>
        <LocationResultsCardCopyCTA address={address} />
      </LI>
    </UL>
  );
});

// ─────────────────────────────────────────────────────────────────────────────

const LocationResultsCardPhoneCTA = memo((props: Required<Pick<Location, 'phone'>>) => {
  const { phone } = props;
  const formattedPhoneNumber = phone.startsWith('+') ? phone : `+${phone}`;

  const { formatMessage } = useIntl();

  return (
    <LocationResultsCardCTALink
      label={formatMessage(messages.locationDetailsCall)}
      iconName="IconPhone"
      href={`tel:${formattedPhoneNumber}`}
    />
  );
});

const LocationResultsCardDirectionsCTA = memo((props: Pick<Location, 'address'>) => {
  const { address } = props;

  const { formatMessage } = useIntl();

  return (
    <LocationResultsCardCTALink
      label={formatMessage(messages.locationDetailsDirections)}
      iconName="IconMap"
      href={getLocationAddressGeolocationLink(address)}
    />
  );
});

const LocationResultsCardUberCTA = memo(
  (props: Required<Pick<Location, 'address' | 'lat' | 'lng'>>) => {
    const { formatMessage } = useIntl();

    return (
      <LocationResultsCardCTALink
        label={formatMessage(messages.locationDetailsUber)}
        iconName="IconCar"
        href={getLocationUberLink(props)}
      />
    );
  },
);

const LocationResultsCardCopyCTA = memo((props: Pick<Location, 'address'>) => {
  const { address } = props;

  const { formatMessage } = useIntl();
  const { push: addBanner } = useNoticeBannersStackActions();

  const copyToClipboard = useCallback(async () => {
    await Clipboard.setStringAsync(address);
    addBanner(
      {
        id: 'location-card-copy-address-banner',
        palette: 'success',
        text: formatMessage(messages.locationDetailsFeedback),
      },
      true,
    );
  }, [addBanner, address, formatMessage]);

  return (
    <LocationResultsCardCTAButton
      label={formatMessage(messages.locationDetailsCopy)}
      iconName="IconCopy"
      onPress={copyToClipboard}
    />
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationDetailsCall: {
    defaultMessage: 'Call',
    description: 'Location | Details | Call',
  },
  locationDetailsDirections: {
    defaultMessage: 'Directions',
    description: 'Location | Details | Directions',
  },
  locationDetailsUber: {
    defaultMessage: 'Request Uber',
    description: 'Location | Details | Uber',
  },
  locationDetailsFeedback: {
    defaultMessage: 'Address copied to your clipboard',
    description: 'Location | Details | Feedback',
  },
  locationDetailsCopy: {
    defaultMessage: 'Copy Address',
    description: 'Location | Details | Copy',
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Returns Uber universal URL for the provided location
 */
function getLocationUberLink(location: Required<Pick<Location, 'address' | 'lng' | 'lat'>>) {
  const { address, lat, lng } = location;

  const uberLinkBase = 'https://m.uber.com/ul';
  const uberLinkSearchParams = {
    action: 'setPickup',
    client_id: 'sweetgreen',
    pickup: 'my_location',
    'dropoff[formatted_address]': encodeURIComponent(address),
    'dropoff[latitude]': lat,
    'dropoff[longitude]': lng,
  };

  return `${uberLinkBase}?${Object.entries(uberLinkSearchParams)
    .map(([key, value]) => `${key}=${value}`)
    .join('&')}`;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  infoCTAList: {
    marginTop: 0,
    marginBottom: theme.spacing['2'],
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  infoCTAListItem: {
    marginRight: 12,
    marginBottom: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultCardCTAsProps = Readonly<{
  location: Location;
}>;
